import { Injectable }       from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { ApiService }                     from '../api/api.service';
import { AttachmentModel }                from './typings/attachment';
import { AttachmentOptionsModel }         from './typings/attachment.options';
import { AttachmentThumbnailStatusModel } from './typings/attachment.thumbnail-status';
import { ConfirmService }                 from '../messages/confirm/confirm.service';
import { ImageViewerService }             from '../shared/components/image-viewer/image-viewer.service';
import { JsonService }                    from '../core/services/json.service';
import { MessengerService }               from '../messages/messenger/messenger.service';
import { SendAttachmentOptionsModel }     from './typings/attachment.send-options';
import { SessionService }                 from '../sessions/session.service';
import { VehicleVisualsService }          from '../vehicle-visuals/vehicle-visuals.service';
import { VideoViewerService }             from '../shared/components/video-viewer/video-viewer.service';

@Injectable({
  providedIn: 'root'
})
export class AttachmentsService {
  constructor (
    private apiService           : ApiService,
    private confirmService       : ConfirmService,
    private imageViewerService   : ImageViewerService,
    private jsonService          : JsonService,
    private messengerService     : MessengerService,
    private sessionService       : SessionService,
    private translateService     : TranslateService,
    private vehicleVisualsService: VehicleVisualsService,
    private videoViewerService   : VideoViewerService
  ) {}

  appendVideoLength ( file : File, data : FormData ) : Promise<void> {
    if ( !this.isVideo(file) ) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const url   = URL.createObjectURL(file);

      const cleanup = () => {
        video.onloadedmetadata = video.onerror = null;

        URL.revokeObjectURL(url);
      };

      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        data.append('minutes', this.getVideoLength(video.duration));

        cleanup();
        resolve();
      };

      video.onerror = () => {
        const error = video.error;

        cleanup();
        reject(error);
      };

      video.src = url;
    });
  }

  deleteAttachment ( attachment : AttachmentModel, options : AttachmentOptionsModel ) : Promise<boolean> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.DELETE_ATTACHMENT'))
      .then(() => this.apiService.delete(options.routes.delete, attachment.id));
  }

  getAttachments ( options : AttachmentOptionsModel ) : Promise<Array<AttachmentModel>> {
    return this.apiService.get(options.routes.get, options.entity.id);
  }

  getThumbnailIcon ( attachment : AttachmentModel ) : string {
    if ( this.isVideo(attachment) ) {
      return '/assets/images/attachment.video.png';
    }

    if ( this.isVehicleVisuals(attachment) ) {
      const isCapricornUser = attachment.vehicle_visuals_capricorn_user !== undefined
        ? !!attachment.vehicle_visuals_capricorn_user
        : this.sessionService.company().vehicle_visuals_capricorn_user;

      return `app/vehicle-visuals/img/${
        isCapricornUser
          ? 'vehicle-visuals.capricorn'
          : 'vehicle-visuals'
      }.svg`;
    }

    return attachment.thumbnail_url || attachment.attachment_url;
  }

  getThumbnails ( status : AttachmentThumbnailStatusModel, attachments : Array<AttachmentModel> | AttachmentModel ) : Promise<void> {
    const images = (Array.isArray(attachments)
      ? attachments
      : [ attachments ])
      .filter(attachment => !this.isPdf(attachment));

    status.isLoading     = true;
    status.isSuccessful  = false;
    status.percentLoaded = 0;

    let loaded = 0;

    return Promise.all(images.map(attachment => {
      attachment.thumbnail_url = this.getThumbnailIcon(attachment);

      return this.loadImage(attachment.thumbnail_url)
        .then(() => {
          status.percentLoaded = Math.ceil(++loaded / images.length * 100);
        });
    }))
      .then(() => {
        status.isSuccessful = true;
      })
      .finally(() => {
        status.isLoading = false;
      });
  }

  getVideoLength ( duration : number ) : string {
    if ( !duration ) {
      return '0';
    }

    return new BigDecimal(duration.toString())
      .divide(new BigDecimal('60'))
      .setScale(0, BigDecimal.ROUND_UP)
      .toString();
  }

  isImage ( attachment : AttachmentModel ) : boolean {
    return attachment.file_type === 'IMAGE';
  }

  isPdf ( attachment : AttachmentModel ) : boolean {
    return attachment.file_type === 'PDF';
  }

  isVehicleVisuals ( attachment : AttachmentModel ) : boolean {
    return attachment.file_type === 'vehicle visuals url';
  }

  isVideo ( attachment : AttachmentModel | File ) : boolean {
    const type = attachment instanceof File
      ? attachment.type
      : attachment.file_type;

    return ( type || '' ).toLowerCase()
      .indexOf('video') >= 0;
  }

  isVideoValid ( video : File ) : boolean {
    const limit = new BigDecimal(this.sessionService.company().upload_limit.toString())
      .multiply(new BigDecimal('1000000'));

    return new BigDecimal(video.size.toString())
      .isLessThanOrEqualTo(limit);
  }

  loadImage ( url : string ) : Promise<void> {
    return new Promise((resolve, reject) => {
      const image = new Image();

      image.onload = () => {
        image.onload = image.onerror = null;

        resolve();
      };

      image.onerror = () => {
        image.onload = image.onerror = null;

        reject(url);
      };

      image.src = url;
    });
  }

  sendVideo ( attachment : AttachmentModel, options : SendAttachmentOptionsModel ) : Promise<any> {
    const company = this.sessionService.company();

    if ( options.method === 'sms' ) {
      return this.messengerService.sms({
        attachment_id   : attachment.id,
        attachment_table: options.table,
        attachment_type : 'video_attachment',
        customer_id     : null,
        disclaimer      : 'Note: The link to access the video will be automatically appended to the end of this text when the message is sent.',
        message         : `View your video from ${ company.company_name }`,
        name            : 'Send Video Attachment',
        send_to         : options.contact
          ? options.contact.phone
          : ''
      });
    }

    return this.messengerService.email({
      attachment_id   : attachment.id,
      attachment_table: options.table,
      attachment_type : 'video_attachment',
      body            : 'View video here: %AttachmentURL%',
      email_address   : options.contact
        ? options.contact.email
        : '',
      name   : 'Send Video Attachment',
      note   : '<span>Note: The link to access the video will be automatically inserted in place of the <strong>%AttachmentURL%</strong> tag</span>',
      subject: `View your video from ${ company.company_name }`
    });
  }

  // Resolves when the viewer is closed. A failed image preload or vehicle-visuals player is ignored,
  // as the AngularJS service did.
  showAttachment ( attachment : AttachmentModel ) : Promise<void> {
    if ( this.isImage(attachment) ) {
      return this.loadImage(attachment.attachment_url)
        .then(() => this.imageViewerService.view(attachment.attachment_url, {
          hasFooter   : true,
          isFullHeight: true,
          isZoomable  : true
        }), () => undefined);
    }

    if ( this.isVideo(attachment) ) {
      return this.videoViewerService.view(attachment.attachment_url);
    }

    if ( this.isVehicleVisuals(attachment) ) {
      return this.vehicleVisualsService.play(attachment.attachment_url, attachment.filename)
        .then(() => undefined, () => undefined);
    }

    window.open(attachment.attachment_url, '_blank', 'noopener,noreferrer');

    return Promise.resolve();
  }

  upload ( file : File, options : AttachmentOptionsModel ) : Promise<Array<AttachmentModel>> {
    if ( this.isVideo(file) && !this.isVideoValid(file) ) {
      return Promise.reject(this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.VIDEO_SETTINGS.VIDEO_IS_TOO_LARGE', {
        size: this.sessionService.company().upload_limit
      }));
    }

    const data = new FormData();

    data.append(options.id_key, options.entity.id);
    data.append('description', options.description);
    data.append('file', file);

    return this.appendVideoLength(file, data)
      .then(() => new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.onload = () => {
          if ( xhr.status < 400 ) {
            resolve();
          }
          else {
            const error = this.jsonService.parse(xhr.responseText);

            reject(error && error.message
              ? error.message
              : this.translateService.instant('JS_SPACE.MESSAGES.UNKNOWN_ERROR_CONTACT_SUPPORT'));
          }
        };

        xhr.onerror = () => reject(this.translateService.instant('JS_SPACE.MESSAGES.UNKNOWN_ERROR_CONTACT_SUPPORT'));

        xhr.open('POST', `${
          this.apiService.getEndpoint()
        }${
          options.routes.create
        }`, true);

        xhr.withCredentials = true;

        xhr.send(data);
      }))
      .then(() => this.getAttachments(options));
  }
}
